import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { useNow } from '@sorrem-client/ui/hooks';
import type { CrucibleRosterEntry } from '@sorrem/shared';
import type { UIPartyEntry } from '@sorrem-client/types';
import { Callout, Icon, useDamageChip } from '@sorrem-client/ui/components';
import { Popover } from '@sorrem/ui';
import classNames from 'classnames';
import { formatFallenCountdown, resolvePartyPanelRows, type PartyPanelMarker, type PartyPanelRow } from './party-panel-rows';
import styles from './PartyPanel.module.scss';
import { isDown } from '@sorrem/shared';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

/** Something a row's menu offers for its member. */
export interface PartyPanelRowAction {
    readonly label: string;
    readonly onSelect: () => void;
    readonly disabled?: boolean;
}

/** Makes a row's marker a toggle (the gathering's Ready, on the viewer's own row). */
export interface PartyPanelMarkerToggle {
    readonly onToggle: () => void;
    /** What pressing it does, for assistive tech, e.g. "Mark yourself ready". */
    readonly ariaLabel: string;
    /** Shown in place of the marker word, e.g. "Ready?" (#237). */
    readonly label?: string;
    /** A first-time callout to the toggle's right, e.g. the first time a player can ready up (#237). */
    readonly callout?: PartyPanelMarkerCallout;
    readonly disabled?: boolean;
}

/** What a marker toggle's callout says, and its Close / Don't show again. */
export interface PartyPanelMarkerCallout {
    readonly label: string;
    readonly onClose: () => void;
    readonly onDismiss: () => void;
}

interface PartyPanelProps {
    /** One row per member shown, in order. */
    rows: readonly PartyPanelRow[];
    /**
     * What a row's menu offers (the gathering's Leave, or the leader's
     * Remove): its 3-dot button, or a right-click on the row, opens it. Rows
     * with no actions have no menu.
     */
    rowActions?: (row: PartyPanelRow) => readonly PartyPanelRowAction[];
    /** Which rows' markers are toggles; null leaves a row's marker a plain word. */
    markerToggle?: (row: PartyPanelRow) => PartyPanelMarkerToggle | null;
}

interface RunPartyPanelProps {
    /** The run's roster, the local player included. */
    roster: readonly CrucibleRosterEntry[];
    /** Every player in the world, from their replicated state. */
    party: readonly UIPartyEntry[];
    /** The party leader's account, to crown their row; null when not known. */
    leaderAccountId?: string | null;
}

/** How long a member who left stays on the panel, marked Left. */
const LEFT_ROW_MS = 4000;

const MARKER_LABELS: Record<Exclude<PartyPanelMarker, 'fallen'>, string> = {
    low: 'Low',
    poisoned: 'Poisoned',
    stunned: 'Stunned',
    burned: 'Burned',
    slowed: 'Slowed',
    disconnected: 'Disconnected',
    joining: 'Joining',
    left: 'Left',
    ready: 'Ready',
    'not-ready': 'Not ready',
};

function formatMarker(row: PartyPanelRow): string | null {
    if (row.marker === null) {
        return null;
    }
    if (row.marker === 'fallen') {
        return row.fallenSecondsLeft === null ? 'Fallen' : `Fallen ${formatFallenCountdown(row.fallenSecondsLeft)}`;
    }
    return MARKER_LABELS[row.marker];
}

/**
 * Keeps each member who drops off the roster on the panel for a moment,
 * marked Left, so the party sees them go.
 */
function useDepartedRows(rows: readonly PartyPanelRow[]): PartyPanelRow[] {
    const [departed, setDeparted] = useState<PartyPanelRow[]>([]);
    const previousRows = useRef<readonly PartyPanelRow[]>(rows);
    const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const rowKeys = rows.map((row) => row.key).join('|');

    useEffect(() => {
        const keys = new Set(rows.map((row) => row.key));
        const gone = previousRows.current.filter((row) => !keys.has(row.key));
        previousRows.current = rows;
        if (gone.length === 0) {
            return;
        }
        setDeparted((current) => [
            ...current.filter((row) => !keys.has(row.key) && !gone.some((left) => left.key === row.key)),
            ...gone.map((row) => ({ ...row, marker: 'left' as const, fallenSecondsLeft: null })),
        ]);
        for (const row of gone) {
            clearTimeout(timers.current.get(row.key));
            timers.current.set(row.key, setTimeout(() => {
                timers.current.delete(row.key);
                setDeparted((current) => current.filter((entry) => entry.key !== row.key));
            }, LEFT_ROW_MS));
        }
        // Only a change in who is on the roster matters here, not every essence tick.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowKeys]);

    useEffect(() => {
        const pending = timers.current;
        return () => {
            for (const timer of pending.values()) {
                clearTimeout(timer);
            }
        };
    }, []);

    return departed;
}

interface PartyMemberRowProps {
    row: PartyPanelRow;
    /** The row's open menu, if any, shown in a popover beside its 3-dot button. */
    menu?: ReactNode;
    /** Opens or closes the row's menu, from its 3-dot button or a right-click; unset, the row has no menu. */
    onMenuToggle?: () => void;
    isMenuOpen?: boolean;
    /** Makes the marker a toggle. */
    markerToggle?: PartyPanelMarkerToggle | null;
}

/**
 * One member's row: crown and name, marker (or exact essence on hover) and a
 * 3-dot menu button over their bar, with the damage chip (#201).
 */
function PartyMemberRow({ row, menu, onMenuToggle, isMenuOpen = false, markerToggle = null }: PartyMemberRowProps): JSX.Element {
    const chip = useDamageChip(row.essence, row.marker !== 'left');
    const [toggleElement, setToggleElement] = useState<HTMLButtonElement | null>(null);
    const toggleAnchorRef = useMemo(() => ({ current: toggleElement }), [toggleElement]);
    const [menuButtonElement, setMenuButtonElement] = useState<HTMLButtonElement | null>(null);
    const menuAnchorRef = useMemo(() => ({ current: menuButtonElement }), [menuButtonElement]);
    const marker = formatMarker(row);
    const toPercent = (amount: number): string => `${row.maxEssence > 0 ? Math.min(1, Math.max(0, amount / row.maxEssence)) * 100 : 0}%`;
    const chipWidth = chip.fromValue === null || chip.draining ? toPercent(row.essence) : toPercent(chip.fromValue);
    const handleContextMenu = onMenuToggle === undefined
        ? undefined
        : (event: MouseEvent): void => {
            event.preventDefault();
            onMenuToggle();
        };
    return (
        <li className={classNames(styles.member, row.marker !== null && styles[row.marker])}>
            <div className={styles.rowBody} onContextMenu={handleContextMenu}>
                <div className={styles.header}>
                    {row.isLeader && <Icon icon="crown" className={styles.crown} strokeWidth={2} tooltip="Party leader" aria-hidden={false} />}
                    <span className={styles.name}>{row.name}</span>
                    {marker !== null && markerToggle === null && <span className={styles.marker}>{marker}</span>}
                    {row.maxEssence > 0 && markerToggle === null && (
                        <span className={styles.numbers}>{`${Math.round(row.essence)} / ${Math.round(row.maxEssence)}`}</span>
                    )}
                    {marker !== null && markerToggle !== null && (
                        <button
                            type="button"
                            ref={setToggleElement}
                            className={classNames(styles.markerToggle, row.marker === 'ready' && styles.checked)}
                            {...toTourTargetProps('ready-toggle')}
                            aria-label={markerToggle.ariaLabel}
                            aria-pressed={row.marker === 'ready'}
                            title={markerToggle.ariaLabel}
                            disabled={markerToggle.disabled}
                            onClick={markerToggle.onToggle}
                        >
                            {/* A checkbox, so it reads as something to press: filled once ready. */}
                            <span className={styles.checkbox}>{row.marker === 'ready' && <Icon icon="accept" strokeWidth={2} />}</span>
                            {markerToggle.label ?? marker}
                        </button>
                    )}
                    {onMenuToggle !== undefined && (
                        <button
                            type="button"
                            ref={setMenuButtonElement}
                            className={classNames(styles.menuButton, isMenuOpen && styles.open)}
                            aria-label={`Actions for ${row.name}`}
                            aria-haspopup="menu"
                            aria-expanded={isMenuOpen}
                            onClick={onMenuToggle}
                        >
                            <Icon icon="menu-vertical" strokeWidth={2} />
                        </button>
                    )}
                </div>
                <div
                    className={styles.bar}
                    role="meter"
                    aria-label={`${row.name}'s essence`}
                    aria-valuemin={0}
                    aria-valuemax={row.maxEssence}
                    aria-valuenow={row.essence}
                >
                    <div className={styles.chip} style={{ width: chipWidth }} />
                    <div className={styles.fill} style={{ width: toPercent(row.essence) }} />
                </div>
            </div>
            {/* To the button's right, so a row low on the screen keeps its menu in view; it flips if there's no room. */}
            {menu && (
                <Popover open anchorRef={menuAnchorRef} placement="right" className={styles.memberMenuPopover}>
                    {menu}
                </Popover>
            )}
            {markerToggle?.callout !== undefined && (
                <Callout
                    open
                    anchorRef={toggleAnchorRef}
                    placement="right"
                    onClose={markerToggle.callout.onClose}
                    onDismiss={markerToggle.callout.onDismiss}
                >
                    {markerToggle.callout.label}
                </Callout>
            )}
        </li>
    );
}

/**
 * The party panel, in the party's HUD area, at the gathering and in the run:
 * a name over a thin essence bar each, with a short colored word (ready,
 * or something wrong such as an affliction or a dropped connection).
 * Hovering a row shows its exact essence. A member who drops off the rows
 * stays a moment, marked Left. The gathering adds row menus and makes the
 * viewer's own marker their Ready toggle.
 */
export function PartyPanel({ rows, rowActions, markerToggle }: PartyPanelProps): JSX.Element | null {
    const departed = useDepartedRows(rows);
    const [menuKey, setMenuKey] = useState<string | null>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    // The open menu, portaled out of the panel in its popover.
    const menuRef = useRef<HTMLDivElement>(null);

    // A click anywhere else closes an open row menu.
    useEffect(() => {
        if (menuKey === null) {
            return;
        }
        const handlePointerDown = (event: PointerEvent): void => {
            const target = event.target as Node;
            if (!panelRef.current?.contains(target) && !menuRef.current?.contains(target)) {
                setMenuKey(null);
            }
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return (): void => document.removeEventListener('pointerdown', handlePointerDown);
    }, [menuKey]);

    const shownRows = [...rows, ...departed];
    if (shownRows.length === 0) {
        return null;
    }

    return (
        <div ref={panelRef} className={styles.partyPanel}>
            <ul className={styles.rows} aria-label="Party">
                {shownRows.map((row) => {
                    // A member who left has nothing left to act on.
                    const actions = row.marker === 'left' ? [] : rowActions?.(row) ?? [];
                    const isMenuOpen = menuKey === row.key && actions.length > 0;
                    return (
                        <PartyMemberRow
                            key={row.key}
                            row={row}
                            markerToggle={row.marker === 'left' ? null : markerToggle?.(row) ?? null}
                            onMenuToggle={actions.length === 0 ? undefined : (): void => setMenuKey(isMenuOpen ? null : row.key)}
                            isMenuOpen={isMenuOpen}
                            menu={isMenuOpen && (
                                <div ref={menuRef} role="menu">
                                    {actions.map((action) => (
                                        <button
                                            key={action.label}
                                            type="button"
                                            role="menuitem"
                                            className={styles.memberMenuItem}
                                            disabled={action.disabled}
                                            onClick={() => {
                                                setMenuKey(null);
                                                action.onSelect();
                                            }}
                                        >
                                            {action.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        />
                    );
                })}
            </ul>
        </div>
    );
}

/**
 * The other party members during a run (#199): the run's roster joined with
 * each player's replicated essence and status. The local player is left out
 * (their essence is on the Loadout), so a solo run shows nothing.
 */
export function RunPartyPanel({ roster, party, leaderAccountId = null }: RunPartyPanelProps): JSX.Element | null {
    // Ticks only while someone fallen has a choice to make, for their countdown.
    const isCountingDown = party.some((member) => !member.isLocal && isDown(member) && member.fallenDecisionDeadlineMs != null);
    const nowMs = useNow(isCountingDown);
    const rows = useMemo(() => resolvePartyPanelRows(roster, party, nowMs, leaderAccountId), [roster, party, nowMs, leaderAccountId]);

    return <PartyPanel rows={rows} />;
}
